(() => {
  /**
   * ZIP Delivery Checker (Overlay)
   * - Multi-instance (multiple sections) supported
   * - Opens via anchors like <a href="#zip-check">
   * - Caches ZIP in localStorage + optional cart attribute
   * - Supports weekly + fortnightly rules:
   *    dayRules: [{ day: "Wednesday", cadence: "weekly|fortnightly", zips: "46201\n46202" }]
   * - Exposes a small public API on window.ZipDelivery for PDP blocks (Part 2):
   *    - getCachedZip(storageKey, ttlDays)
   *    - getDaysForZip(zip, { triggerId })
   *    - openChecker(triggerId)
   *    - resetCheckerUI(triggerId)  // hides result + actions ("Shop now"/"Change ZIP") + returns to initial state
   * - Emits: window.dispatchEvent(new CustomEvent("zipdelivery:updated", { detail: { zip, triggerId, served, days } }))
   */

  const MODAL_SELECTOR = "[data-zipcheck-modal]";
  const CONFIG_SELECTOR = "[data-zipcheck-config]";
  const CLOSE_SELECTOR = "[data-zipcheck-close]";
  const FORM_SELECTOR = "[data-zipcheck-form]";
  const INPUT_SELECTOR = "[data-zipcheck-input]";
  const RESULT_SELECTOR = "[data-zipcheck-result]";
  const ACTIONS_SELECTOR = "[data-zipcheck-actions]";
  const PRIMARY_SELECTOR = "[data-zipcheck-primary]";
  const SECONDARY_SELECTOR = "[data-zipcheck-secondary]";

  const DEFAULTS = {
    triggerId: "zip-check",
    storageKey: "site_zip",
    ttlDays: 30,
    servedCtaUrl: "/collections/all",
    notServedUrl: "/pages/shipping",
    servedPrimaryText: "Shop now",
    servedSecondaryText: "Change ZIP",
    notServedPrimaryText: "See delivery options",
    notServedSecondaryText: "Try another ZIP",
    servedMessage:
      "Good news — we deliver to {zip}.<br><strong>Delivery days:</strong> {days}",
    notServedMessage: "Sorry — we don’t currently deliver to {zip}.",
    setCartAttribute: true,
    cartAttributeKey: "delivery_zip",
    autoOpenIfNoZip: false,
  };

  const isDesignMode =
    document.documentElement.classList.contains("shopify-design-mode") ||
    document.documentElement.classList.contains("shopify-design_mode");

  // -----------------------
  // Helpers
  // -----------------------
  const safeJSON = (el) => {
    if (!el) return null;
    try {
      return JSON.parse((el.textContent || "").trim());
    } catch (_) {
      return null;
    }
  };

  const normalizeZip = (zipRaw) => String(zipRaw || "").replace(/\s+/g, "").trim();

  const parseZipList = (text) =>
    String(text || "")
      .split(/[\s,]+/g)
      .map((z) => normalizeZip(z))
      .filter((z) => z && z.length >= 4);

  const normalizeCadence = (cadenceRaw) => {
    const c = String(cadenceRaw || "weekly").trim().toLowerCase();
    if (
      c === "fortnightly" ||
      c === "every2nd" ||
      c === "every_2nd" ||
      c === "every-2nd"
    )
      return "fortnightly";
    return "weekly";
  };

  const labelForRule = (day, cadence) => {
    const baseDay = String(day || "").trim();
    if (!baseDay) return "";
    return normalizeCadence(cadence) === "fortnightly"
      ? `Every 2nd ${baseDay}`
      : baseDay;
  };

  const formatDays = (days) => (Array.isArray(days) ? days : []).join(", ");

  const replaceTokens = (template, data) =>
    String(template || "")
      .replaceAll("{zip}", data.zip || "")
      .replaceAll("{days}", formatDays(data.days || []));

  // Some themes override [hidden]. Force display none when hidden.
  const setHidden = (el, hidden) => {
    if (!el) return;
    el.hidden = !!hidden;
    el.style.display = hidden ? "none" : "";
  };

    // -----------------------
  // ZIP submission logging
  // -----------------------
  const ZIP_LOG_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbwbB9g0QSVAdAc5mw3ArO3mwbI307LrUhn_-Go9fREOKOb1QiQtWDy1RdDYpy-tl4rAcQ/exec";

  const logZipSubmission = async ({ zip, served, days, triggerId, source }) => {
    if (!ZIP_LOG_WEBHOOK_URL || ZIP_LOG_WEBHOOK_URL.includes("PASTE_YOUR")) return;

    try {
      await fetch(ZIP_LOG_WEBHOOK_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify({
          submittedAt: new Date().toISOString(),
          zip,
          served: !!served,
          days: Array.isArray(days) ? days : [],
          triggerId: triggerId || "",
          source: source || "overlay",
          pageUrl: window.location.href,
          userAgent: navigator.userAgent || ""
        }),
      });
    } catch (_) {}
  };

  // -----------------------
  // Cache
  // -----------------------
  const getZipCache = (storageKey, ttlDays) => {
    const key = storageKey || DEFAULTS.storageKey;
    const tsKey = `${key}_ts`;

    const zip = localStorage.getItem(key);
    const ts = Number(localStorage.getItem(tsKey) || 0);
    if (!zip) return null;

    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    if (ageDays > Number(ttlDays || DEFAULTS.ttlDays)) {
      localStorage.removeItem(key);
      localStorage.removeItem(tsKey);
      return null;
    }
    return zip;
  };

  const setZipCache = async (cfg, zip) => {
    const key = cfg.storageKey || DEFAULTS.storageKey;
    const tsKey = `${key}_ts`;

    localStorage.setItem(key, zip);
    localStorage.setItem(tsKey, String(Date.now()));

    if (cfg.setCartAttribute) {
      try {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attributes: { [cfg.cartAttributeKey || DEFAULTS.cartAttributeKey]: zip },
          }),
        });
      } catch (_) {}
    }
  };

  // -----------------------
  // Zip rules
  // -----------------------
  const buildZipDayIndex = (cfg) => {
    // zip -> { set: Set(days), order: [] } preserves first-seen order
    const rules = Array.isArray(cfg.dayRules) ? cfg.dayRules : [];
    const index = new Map();

    rules.forEach((r) => {
      const label = labelForRule(r?.day, r?.cadence);
      if (!label) return;

      const zips = parseZipList(r?.zips);
      zips.forEach((zip) => {
        if (!index.has(zip)) index.set(zip, { set: new Set(), order: [] });
        const entry = index.get(zip);
        if (!entry.set.has(label)) {
          entry.set.add(label);
          entry.order.push(label);
        }
      });
    });

    return index;
  };

  const lookupZip = (inst, zipRaw) => {
    const zip = normalizeZip(zipRaw);
    if (!zip) return { served: false, days: [] };

    const entry = inst?._zipIndex?.get(zip);
    if (entry?.order?.length) return { served: true, days: entry.order.slice() };
    return { served: false, days: [] };
  };

  // -----------------------
  // Scroll lock
  // -----------------------
  const openSet = new Set();
  const scrollLockState = { locked: false, bodyOverflow: "", bodyPaddingRight: "" };

  const getScrollbarWidth = () =>
    Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const lockScroll = () => {
    if (isDesignMode) return;
    if (scrollLockState.locked) return;

    scrollLockState.locked = true;
    scrollLockState.bodyOverflow = document.body.style.overflow;
    scrollLockState.bodyPaddingRight = document.body.style.paddingRight;

    const sbw = getScrollbarWidth();
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;

    document.body.style.overflow = "hidden";
  };

  const unlockScroll = () => {
    if (isDesignMode) return;
    if (!scrollLockState.locked) return;

    scrollLockState.locked = false;
    document.body.style.overflow = scrollLockState.bodyOverflow;
    document.body.style.paddingRight = scrollLockState.bodyPaddingRight;
  };

  const unlockScrollIfNoneOpen = () => {
    if (openSet.size === 0) unlockScroll();
  };

  // -----------------------
  // Collect instances
  // -----------------------
  const modals = Array.from(document.querySelectorAll(MODAL_SELECTOR));
  if (!modals.length) return;

  const instances = modals
    .map((modal) => {
      const cfgRaw = safeJSON(modal.querySelector(CONFIG_SELECTOR));
      if (!cfgRaw) return null;

      const config = { ...DEFAULTS, ...cfgRaw };

      const form = modal.querySelector(FORM_SELECTOR);
      const input = modal.querySelector(INPUT_SELECTOR);
      const result = modal.querySelector(RESULT_SELECTOR);
      const actions = modal.querySelector(ACTIONS_SELECTOR);
      const primary = modal.querySelector(PRIMARY_SELECTOR);
      const secondary = modal.querySelector(SECONDARY_SELECTOR);

      if (!form || !input || !result || !actions || !primary || !secondary) return null;

      const inst = {
        modal,
        config,
        form,
        input,
        result,
        actions,
        primary,
        secondary,
        _zipIndex: buildZipDayIndex(config),
        resetUI: null, // assigned below
      };

      return inst;
    })
    .filter(Boolean);

  if (!instances.length) return;

  const getInstanceByTrigger = (triggerId) => {
    const id = triggerId || DEFAULTS.triggerId;
    return (
      instances.find((x) => (x.config.triggerId || DEFAULTS.triggerId) === id) ||
      instances[0]
    );
  };

  const getInstanceBySectionId = (sectionId) => {
    if (!sectionId) return null;
    return instances.find((x) => String(x.config.sectionId) === String(sectionId)) || null;
  };

  // -----------------------
  // Open / close
  // -----------------------
  const openModal = (inst) => {
    if (!inst?.modal) return;

    inst.modal.classList.add("is-open");
    inst.modal.setAttribute("aria-hidden", "false");
    openSet.add(inst.modal);
    lockScroll();

    // Always reset to initial UI on open
    inst.resetUI?.({ keepInput: true, selectInput: false });

    // Prefill from cache
    const cached = getZipCache(inst.config.storageKey, inst.config.ttlDays);
    if (cached) inst.input.value = cached;

    setTimeout(() => inst.input?.focus(), 50);
  };

  const closeModal = (inst) => {
    if (!inst?.modal) return;

    inst.modal.classList.remove("is-open");
    inst.modal.setAttribute("aria-hidden", "true");
    openSet.delete(inst.modal);
    unlockScrollIfNoneOpen();
  };

  const closeAll = () => {
    instances.forEach((inst) => {
      if (inst.modal.classList.contains("is-open")) closeModal(inst);
    });
  };

  // -----------------------
  // Bind per instance
  // -----------------------
  instances.forEach((inst) => {
    const { modal, config: cfg, form, input, result, actions, primary, secondary } =
      inst;

    const resetUI = ({ keepInput = true, selectInput = true } = {}) => {
      // Hide both result + actions wrapper (so both buttons disappear)
      setHidden(result, true);
      result.innerHTML = "";

      setHidden(actions, true);

      // Remove any previous click handlers to avoid stacking
      primary.onclick = null;
      secondary.onclick = null;

      if (!keepInput) input.value = "";

      if (selectInput) {
        setTimeout(() => {
          input.focus();
          input.select();
        }, 0);
      }
    };

    inst.resetUI = resetUI;

    // Initial state
    resetUI({ keepInput: true, selectInput: false });

    // Close (X button)
    modal.addEventListener("click", (e) => {
      if (e.target && e.target.matches(CLOSE_SELECTOR)) closeModal(inst);
    });

    const attachSecondaryReset = (labelText) => {
      secondary.textContent = labelText;
      secondary.href = "#";
      secondary.onclick = (ev) => {
        ev.preventDefault();
        // You asked: when changing ZIP, close the good/bad news + show initial UI
        resetUI({ keepInput: true, selectInput: true });
      };
    };

    const renderServed = (zip, days) => {
      setHidden(result, false);
      result.innerHTML = replaceTokens(cfg.servedMessage, { zip, days });

      setHidden(actions, false);

      primary.textContent = cfg.servedPrimaryText || DEFAULTS.servedPrimaryText;
      primary.href = cfg.servedCtaUrl || DEFAULTS.servedCtaUrl;
      primary.onclick = null;

      attachSecondaryReset(cfg.servedSecondaryText || DEFAULTS.servedSecondaryText);
    };

    const renderNotServed = (zip) => {
      setHidden(result, false);
      result.innerHTML = replaceTokens(cfg.notServedMessage, { zip, days: [] });

      setHidden(actions, false);

      primary.textContent = cfg.notServedPrimaryText || DEFAULTS.notServedPrimaryText;
      primary.href = cfg.notServedUrl || DEFAULTS.notServedUrl;
      primary.onclick = null;

      attachSecondaryReset(cfg.notServedSecondaryText || DEFAULTS.notServedSecondaryText);
    };

    const renderValidationError = (message) => {
      setHidden(result, false);
      result.textContent = message || "Please enter a valid ZIP code.";
      setHidden(actions, true);
    };

    // Prefill from cache on load
    const cached = getZipCache(cfg.storageKey, cfg.ttlDays);
    if (cached) input.value = cached;

    // Auto-open
    if (!isDesignMode && !cached && cfg.autoOpenIfNoZip) openModal(inst);

    // Submit
    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const zip = normalizeZip(input.value);
      if (!zip || zip.length < 4) {
        renderValidationError("Please enter a valid ZIP code.");
        return;
      }

      // Ensure buttons stay hidden until we render served/not served
      setHidden(actions, true);

      await setZipCache(cfg, zip);

      const match = lookupZip(inst, zip);
      if (match.served) renderServed(zip, match.days);
      else renderNotServed(zip);

      logZipSubmission({
        zip,
        served: !!match.served,
        days: match.days || [],
        triggerId: cfg.triggerId || DEFAULTS.triggerId,
        source: "overlay"
      });

      // Notify PDP blocks + any other listeners
      window.dispatchEvent(
        new CustomEvent("zipdelivery:updated", {
          detail: {
            zip,
            triggerId: cfg.triggerId || DEFAULTS.triggerId,
            served: !!match.served,
            days: match.days || [],
          },
        })
      );
    });
  });

  // ESC closes
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeAll();
  });

  // Anchor triggers
  document.addEventListener("click", (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;

    const hash = a.getAttribute("href");
    if (!hash || hash.length < 2) return;

    const triggerId = hash.slice(1);
    const valid = instances.some(
      (x) => (x.config.triggerId || DEFAULTS.triggerId) === triggerId
    );
    if (!valid) return;

    e.preventDefault();
    openModal(getInstanceByTrigger(triggerId));
  });

  // Theme editor behaviour
  if (isDesignMode) {
    document.addEventListener("shopify:section:select", (event) => {
      const inst = getInstanceBySectionId(event?.detail?.sectionId);
      if (inst) openModal(inst);
    });

    document.addEventListener("shopify:section:deselect", (event) => {
      const inst = getInstanceBySectionId(event?.detail?.sectionId);
      if (inst) closeModal(inst);
    });
  }

  // -----------------------
  // Public API for PDP blocks
  // -----------------------
  window.ZipCache = {
    get: (storageKey = DEFAULTS.storageKey, ttlDays = DEFAULTS.ttlDays) =>
      getZipCache(storageKey, ttlDays),
  };

  window.ZipDelivery = window.ZipDelivery || {};

  window.ZipDelivery.getCachedZip = (storageKey = DEFAULTS.storageKey, ttlDays = DEFAULTS.ttlDays) =>
    getZipCache(storageKey, ttlDays);

  window.ZipDelivery.openChecker = (triggerId = DEFAULTS.triggerId) => {
    const inst = getInstanceByTrigger(triggerId);
    if (!inst) return;
    openModal(inst);
  };

  window.ZipDelivery.resetCheckerUI = (triggerId = DEFAULTS.triggerId) => {
    const inst = getInstanceByTrigger(triggerId);
    if (!inst) return;
    inst.resetUI?.({ keepInput: true, selectInput: true });
  };

  window.ZipDelivery.getDaysForZip = (zip, opts = {}) => {
    const triggerId = opts.triggerId || DEFAULTS.triggerId;
    const inst = getInstanceByTrigger(triggerId);
    if (!inst) return [];
    const match = lookupZip(inst, zip);
    return match?.served ? match.days : [];
  };
})();
